/* ============================================================================
   RGS Backoffice — Mantine theme (SOURCE OF TRUTH)
   ----------------------------------------------------------------------------
   Every RGS design token, expressed as a Mantine theme override + a CSS-
   variables resolver. Real Mantine components consume this, so upgrading
   Mantine carries the design forward. Warm, dense, ink-on-paper; one warm
   accent (rgsOrange) used sparingly. See backoffice/.claude/skills/design-system.

   Status uses the muted RGS ramps directly: red / yellow / green / blue.
   ============================================================================ */

import { createTheme, type CSSVariablesResolver } from '@mantine/core';

const rgsOrange = [
    '#FDF0E6',
    '#F9DCC7',
    '#F2C09B',
    '#EBA471',
    '#E68C4D',
    '#F1832F',
    '#E0701F',
    '#B0530E',
    '#8E4209',
    '#6E3206',
] as const;
const red = [
    '#FBEAE6',
    '#F6D2C9',
    '#EBA597',
    '#E07C66',
    '#D55A3F',
    '#C8472F',
    '#A93A26',
    '#88301F',
    '#682619',
    '#4D1C13',
] as const;
const yellow = [
    '#FBF1E1',
    '#F5DFBD',
    '#EBC074',
    '#E0A53A',
    '#C77D17',
    '#A96714',
    '#895311',
    '#6B410D',
    '#4F300A',
    '#3A2407',
] as const;
const green = [
    '#EAF3E5',
    '#D6E8CC',
    '#B3D3A1',
    '#8FBE75',
    '#6BA94A',
    '#4F8C3C',
    '#447A34',
    '#38632A',
    '#2C4D21',
    '#213A19',
] as const;
const blue = [
    '#E9F0F8',
    '#CFDDEF',
    '#A6C0DF',
    '#7BA1CE',
    '#5285BE',
    '#3A6FB0',
    '#315E96',
    '#274B78',
    '#1D385A',
    '#15293F',
] as const;

export const theme = createTheme({
    /* one warm accent, used sparingly — primary actions, focus, selection */
    primaryColor: 'rgsOrange',
    primaryShade: { light: 6, dark: 5 },

    white: '#FFFFFF',
    black: '#1A1712',
    defaultRadius: 'md',
    focusRing: 'auto',
    cursorType: 'pointer',

    colors: {
        rgsOrange: [...rgsOrange],
        /* warm neutral grays — borders, muted text, wells */
        gray: [
            '#F7F5F1',
            '#F0EDE6',
            '#E7E3DA',
            '#D8D3C8',
            '#C3BDB0',
            '#A8A293',
            '#8E887C',
            '#5C564C',
            '#36322B',
            '#1A1712',
        ],
        /* warm dark surfaces for the dark theme */
        dark: [
            '#F3EFE7',
            '#D9D3C8',
            '#ADA699',
            '#8A8276',
            '#726C61',
            '#3A372F',
            '#2E2B23',
            '#1C1A16',
            '#161512',
            '#131210',
        ],
        /* status — muted, never loud */
        green: [...green],
        red: [...red],
        yellow: [...yellow],
        blue: [...blue],
    },

    /* two families; hierarchy from face + size + ink, not weight */
    fontFamily: "'Google Sans', system-ui, -apple-system, 'Segoe UI', sans-serif",
    fontFamilyMonospace: "ui-monospace, 'SF Mono', 'JetBrains Mono', Menlo, monospace",
    headings: {
        fontFamily: "'ABC Social Condensed', 'Google Sans', sans-serif",
        fontWeight: '400',
        sizes: {
            h1: { fontSize: '27px', lineHeight: '1.05' },
            h2: { fontSize: '20px', lineHeight: '1.1' },
            h3: { fontSize: '17px', lineHeight: '1.15' },
            h4: { fontSize: '15px', lineHeight: '1.2' },
            h5: { fontSize: '13px', lineHeight: '1.25' },
            h6: { fontSize: '11.5px', lineHeight: '1.3' },
        },
    },

    /* compact scale — 13px base */
    fontSizes: { xs: '11.5px', sm: '12.5px', md: '13px', lg: '15px', xl: '18px' },
    lineHeights: { xs: '1.35', sm: '1.4', md: '1.5', lg: '1.55', xl: '1.6' },

    /* soft corners */
    radius: { xs: '5px', sm: '7px', md: '9px', lg: '12px', xl: '14px' },

    /* 4px base step */
    spacing: { xs: '8px', sm: '12px', md: '16px', lg: '20px', xl: '26px' },

    /* minimal elevation */
    shadows: {
        xs: '0 1px 2px rgba(26,23,18,0.06)',
        sm: '0 1px 2px rgba(26,23,18,0.06)',
        md: '0 1px 2px rgba(26,23,18,0.05), 0 8px 26px rgba(26,23,18,0.07)',
        lg: '0 12px 40px rgba(26,23,18,0.16)',
        xl: '0 16px 48px rgba(26,23,18,0.16)',
    },

    /* component defaults lock the look in */
    components: {
        Button: { defaultProps: { fw: 500 } },
        Card: { defaultProps: { withBorder: true, shadow: 'sm', radius: 'lg' } },
        Paper: { defaultProps: { radius: 'lg' } },
        Badge: { defaultProps: { radius: 'sm', variant: 'light', tt: 'none', fw: 500 } },
        TextInput: { defaultProps: { radius: 'md' } },
        Select: { defaultProps: { radius: 'md' } },
        NavLink: { defaultProps: { fw: 400 } },
        Table: { defaultProps: { horizontalSpacing: 'md', verticalSpacing: 'sm' } },
        Tooltip: { defaultProps: { radius: 'sm', color: 'dark' } },
        Code: { defaultProps: { color: 'gray' } },
    },
});

/* Pin the warm surfaces/ink/borders that Mantine derives from its gray/dark
   scales, so paper + ink read exactly. Also exposes --rgs-* helpers. */
export const rgsCssVariablesResolver: CSSVariablesResolver = () => ({
    // :root fallbacks (light values) so the body background + surfaces are defined
    // on the very first paint, before the color-scheme attribute is applied.
    variables: {
        '--rgs-bg': '#FBFAF7',
        '--rgs-surface': '#FFFFFF',
        '--rgs-surface-2': '#F4F2EC',
        '--rgs-line': 'rgba(26,23,18,0.10)',
        '--rgs-line-2': 'rgba(26,23,18,0.055)',
    },
    light: {
        '--mantine-color-text': '#1A1712',
        '--mantine-color-dimmed': '#8E887C',
        '--mantine-color-default': '#FFFFFF',
        '--mantine-color-default-hover': '#F4F2EC',
        '--mantine-color-default-border': 'rgba(26,23,18,0.10)',
        '--mantine-color-default-color': '#1A1712',
        '--rgs-bg': '#FBFAF7',
        '--rgs-surface': '#FFFFFF',
        '--rgs-surface-2': '#F4F2EC',
        '--rgs-line': 'rgba(26,23,18,0.10)',
        '--rgs-line-2': 'rgba(26,23,18,0.055)',
    },
    dark: {
        '--mantine-color-body': '#1C1A16',
        '--mantine-color-text': '#F3EFE7',
        // dark[2] (was dark[4] #726C61): lifts secondary/dimmed text — table IDs,
        // column headers, input descriptions — to ~7:1 on the dark surface so it
        // reads clearly, while staying visibly muted vs the #F3EFE7 primary ink.
        '--mantine-color-dimmed': '#ADA699',
        // red[3] (default red-filled ~red[5] is only ~3.6:1): lifts validation
        // errors — message text, invalid input value + border — to ~6:1 on the
        // dark surface so "Invalid …" reads clearly, still a firm error red.
        '--mantine-color-error': '#E07C66',
        '--mantine-color-default': '#1C1A16',
        '--mantine-color-default-hover': '#24221C',
        '--mantine-color-default-border': 'rgba(255,255,255,0.12)',
        '--mantine-color-default-color': '#F3EFE7',
        '--rgs-bg': '#131210',
        '--rgs-surface': '#1C1A16',
        '--rgs-surface-2': '#24221C',
        '--rgs-line': 'rgba(255,255,255,0.12)',
        '--rgs-line-2': 'rgba(255,255,255,0.06)',
    },
});
